import IPage from 'interfaces/page'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'app/hooks'
import { loginAction, loginCleverAction } from 'app/reducer/authSlice'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as Yup from 'yup'
import { clearError, selectErrors, setError } from 'app/reducer/errorSlice'
import { useAppSelector } from 'app/hooks'
import {
  selectIsLoading,
  selectIsLoadingClever,
  setIsLoadingClever
} from 'app/reducer/pageSlice'
import Layout from 'common/Layout'
import config from 'config/config'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled, { css, ThemeProvider } from 'styled-components'
import loginBackground from 'assets/img/apo26-student-portal-bg.jpg'
import loginLogo from 'assets/img/apo26-student-portal-logo.svg'
import { AppTheme, getThemepalette } from 'config/theme'

const LoginShell = styled.section`
  position: relative;
  min-height: 100vh;
  overflow-x: hidden;
  background-color: ${(props) => props.theme.colors.card.background};
  background-image: url(${loginBackground});
  background-position: center center;
  background-size: cover;
  color: ${(props) => props.theme.colors.text.primary};
  font-family: ${(props) => props.theme.typography.fontNunito};

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    background-position: 16% center;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    background-position: 0% center;
  }
`

const LoginScrim = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    color-mix(
        in srgb,
        ${(props) => props.theme.colors.card.background} 34%,
        transparent
      )
      0%,
    transparent 34%,
    color-mix(
        in srgb,
        ${(props) => props.theme.colors.text.primary} 12%,
        transparent
      )
      100%
  );
  pointer-events: none;
`

const LoginPageFrame = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  min-height: 100vh;
  flex-direction: column;
  padding: 82px 32px 0;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 28px 12px 0;
  }
`

const Brand = styled.div`
  width: min(295px, calc(100vw - 48px));
  margin-left: max(104px, calc((100vw - 1240px) / 2 + 24px));

  img {
    display: block;
    width: 100%;
    height: auto;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    margin-left: 24px;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    margin-right: auto;
    margin-left: auto;
  }
`

const LoginMain = styled.main`
  display: flex;
  width: min(100%, 1240px);
  flex: 1;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  padding: 86px 0 102px;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 48px 0 56px;
  }
`

const LoginCard = styled.div`
  width: min(405px, calc(100vw - 24px));
  overflow: hidden;
  border: 1px solid ${(props) => props.theme.colors.border.primary};
  border-radius: 8px;
  background: color-mix(
    in srgb,
    ${(props) => props.theme.colors.card.background} 94%,
    transparent
  );
  box-shadow: 0 16px 40px
    color-mix(
      in srgb,
      ${(props) => props.theme.colors.text.primary} 18%,
      transparent
    );
`

const LoginCardBody = styled.div`
  padding: 15px 18px 14px;
`

const LoginTitle = styled.h1`
  margin: 0 0 14px;
  color: ${(props) => props.theme.colors.text.primary};
  font-family: ${(props) => props.theme.typography.fontPlusJakartaSans};
  font-size: 23px;
  font-weight: 700;
  line-height: 30px;
`

const LoginForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 0;
`

const FieldGroup = styled.div`
  position: relative;
  margin-bottom: 8px;
  text-align: left;
`

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 5px;
  color: ${(props) => props.theme.colors.text.primary};
  font-family: ${(props) => props.theme.typography.fontNunito};
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
`

const TextInput = styled.input`
  width: 100%;
  height: 38px;
  padding: 6px 32px 7px 13px;
  border: 1px solid ${(props) => props.theme.colors.border.primary};
  border-radius: 8px;
  background: color-mix(
    in srgb,
    ${(props) => props.theme.colors.card.background} 82%,
    transparent
  );
  color: ${(props) => props.theme.colors.text.primary};
  font-family: ${(props) => props.theme.typography.fontNunito};
  font-size: 15px;
  font-weight: 400;
  line-height: 22px;

  &::placeholder {
    color: ${(props) => props.theme.colors.text.paragraph};
  }

  &:focus,
  &:focus-visible {
    border-color: ${(props) => props.theme.colors.border.info};
    box-shadow: 0 0 0 2px
      color-mix(
        in srgb,
        ${(props) => props.theme.colors.border.info} 18%,
        transparent
      );
    outline: none;
  }

  &.is-invalid {
    border-color: ${(props) => props.theme.colors.text.error};
  }
`

const StatusDot = styled.span<{ $hasError: boolean; $visible: boolean }>`
  display: ${(props) => (props.$visible ? 'block' : 'none')};
  position: absolute;
  right: 12px;
  top: 37px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.$hasError
      ? props.theme.colors.surface.error
      : props.theme.colors.surface.success};
`

const FieldError = styled.div`
  margin-top: 4px;
  color: ${(props) => props.theme.colors.text.error};
  font-family: ${(props) => props.theme.typography.fontNunito};
  font-size: 12px;
  line-height: 16px;
`

const ButtonPanel = styled.div`
  --login-submit-width: 76px;
  --login-button-gap: 12px;
  --login-sso-width: 229px;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: calc(
    var(--login-sso-width) + var(--login-button-gap) + var(--login-submit-width)
  );
  max-width: 100%;
  margin-top: 7px;
  margin-left: auto;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    --login-button-gap: 10px;

    width: 100%;
    margin-left: 0;
  }
`

const ButtonStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`

const ButtonRow = styled.div<{ $centered?: boolean }>`
  display: grid;
  grid-template-columns: var(--login-sso-width) var(--login-submit-width);
  width: 100%;
  align-items: center;
  column-gap: var(--login-button-gap);
  padding-right: 0;

  > a {
    justify-self: end;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    grid-template-columns: minmax(0, 1fr) var(--login-submit-width);
  }
`

const SsoButton = styled.a<{ $provider: 'clever' | 'classlink' }>`
  display: inline-flex;
  width: ${(props) =>
    props.$provider === 'clever' ? '209px' : 'var(--login-sso-width)'};
  height: 33px;
  align-items: center;
  justify-content: flex-start;
  padding: 0 12px;
  border: 1px solid ${(props) => props.theme.colors.border.primary};
  border-radius: 18px;
  background: ${(props) => props.theme.colors.card.background};
  color: ${(props) =>
    props.$provider === 'clever'
      ? props.theme.colors.text.clever
      : props.theme.colors.surface.infoDark};
  font-family: ${(props) => props.theme.typography.fontNunito};
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  text-decoration: none;
  transition:
    background-color ${(props) => props.theme.animation.duration}
      ${(props) => props.theme.animation.style},
    border-color ${(props) => props.theme.animation.duration}
      ${(props) => props.theme.animation.style},
    color ${(props) => props.theme.animation.duration}
      ${(props) => props.theme.animation.style};

  span {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
  }

  span:first-child {
    flex: 0 0 31px;
    width: 31px;
    justify-content: center;
  }

  span:last-child {
    flex: 1;
    justify-content: center;
  }

  hr {
    flex: 0 0 1px;
    width: 1px;
    height: 23px;
    margin: 0 12px 0 10px;
    border: 0;
    background-color: currentColor;
    opacity: 1;
  }

  svg > path,
  svg g path {
    fill: currentColor;
  }

  svg {
    flex: 0 0 auto;
    max-width: 23px;
  }

  ${(props) =>
    props.$provider === 'clever' &&
    css`
      span:first-child {
        color: ${props.theme.colors.text.clever};
      }

      svg {
        max-width: 15px;
      }

      svg g path {
        fill: ${props.theme.colors.text.clever};
      }
    `}

  &:hover,
  &:focus-visible {
    border-color: currentColor;
    background-color: ${(props) => props.theme.colors.card.background};
    color: ${(props) =>
      props.$provider === 'clever'
        ? props.theme.colors.text.clever
        : props.theme.colors.surface.infoDark};
    outline: none;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    width: ${(props) =>
      props.$provider === 'clever' ? '209px' : 'var(--login-sso-width)'};
    min-width: 0;
    font-size: 14px;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    width: 100%;
    max-width: none;
  }
`

const SubmitButton = styled.button`
  display: inline-flex;
  width: 76px;
  height: 33px;
  align-items: center;
  justify-content: center;
  padding: 0 15px;
  border: 0;
  border-radius: 18px;
  background: color-mix(
    in srgb,
    ${(props) => props.theme.colors.surface.infoDark} 38%,
    transparent
  );
  color: ${(props) => props.theme.colors.card.background};
  font-family: ${(props) => props.theme.typography.fontNunito};
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;

  &:not(:disabled):not([aria-disabled='true']) {
    background-color: ${(props) => props.theme.colors.surface.infoDark};
  }

  &:hover:not(:disabled):not([aria-disabled='true']),
  &:focus-visible:not(:disabled):not([aria-disabled='true']) {
    background-color: ${(props) => props.theme.colors.text.primary};
    outline: none;
  }

  &:disabled,
  &[aria-disabled='true'] {
    cursor: not-allowed;
  }
`

const LoginAlert = styled(Alert)`
  margin-bottom: 12px;
  font-size: 13px;
  text-align: left;
`

const LegalFooter = styled.footer`
  width: min(100%, 1240px);
  margin: 0 auto;
  border: 1px solid ${(props) => props.theme.colors.border.primary};
  border-bottom: 0;
  border-radius: 8px 8px 0 0;
  background: ${(props) => props.theme.colors.card.surface};
  padding: 14px 36px 20px;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 14px 18px 18px;
  }
`

const Copyright = styled.p`
  margin: 0 0 12px;
  color: ${(props) => props.theme.colors.text.primary};
  font-family: ${(props) => props.theme.typography.fontNunito};
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
`

const LegalText = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.text.primary};
  font-family: ${(props) => props.theme.typography.fontNunito};
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: left;

  a {
    color: ${(props) => props.theme.colors.text.info};
  }
`

const isValidStudentId = (studentId: string) => /^\d{4,5}$/.test(studentId)

const LoginPage: React.FunctionComponent<IPage> = (props) => {
  const dispatch = useAppDispatch()
  const errors = useSelector(selectErrors)
  const isLoading = useAppSelector(selectIsLoading)
  const isLoadingClever = useAppSelector(selectIsLoadingClever)
  const [hasLoginAttempted, setHasLoginAttempted] = useState(false)
  const [searchParams] = useSearchParams()
  const cleverId = searchParams.get('clever_id')
  const classlinkId = searchParams.get('classlink_id')
  const cleverLoginAttemptRef = useRef<string | null>(null)

  const errorMsg = searchParams.get('error_msg')
  const navigate = useNavigate()

  const initialValues = {
    last_name: '',
    student_id: '',
    password: ''
  }
  useEffect(() => {
    return () => {
      dispatch(clearError())
    }
  }, [dispatch])

  useEffect(() => {
    if (errorMsg) {
      dispatch(setError(errorMsg))
      return
    }

    const loginCode = classlinkId || cleverId
    if (!loginCode || cleverLoginAttemptRef.current === loginCode) {
      return
    }

    /**
     * Keep the callback code in a ref before dispatching so the state update
     * from setIsLoadingClever cannot trigger the same login request again.
     */
    cleverLoginAttemptRef.current = loginCode
    dispatch(setIsLoadingClever())
    dispatch(
      loginCleverAction(
        {
          code: loginCode,
          type: classlinkId ? 'classlink' : 'clever'
        },
        navigate
      )
    )
  }, [classlinkId, cleverId, dispatch, errorMsg, navigate])

  const onSubmit = (values: any) => {
    const trimedValues = {
      last_name: values.last_name.trim(),
      student_id: values.student_id.trim(),
      password: values.password.trim()
    }
    dispatch(loginAction(trimedValues, navigate))
  }

  const validationSchema = Yup.object({
    last_name: Yup.string().required('Last Name is required'),
    // student_id: Yup.number().required("Student Id is required").test('len', 'Must be five digits only!', val => val?.toString().length === 5),
    // student_id: Yup.string().length(5, 'Must be five digits only!').matches(/[0-9]+/gi, "Must be five digits only!").required('Student Id is required')
    student_id: Yup.string()
      .required('Student ID is required')
      .matches(/^\d+$/, '4 digits are required')
      .test(
        'len',
        '4 digits are required',
        (val) => val?.toString().length === 5 || val?.toString().length === 4
      )
  })

  /**
   * The student login screen should always render with the light palette,
   * even when the rest of the portal is currently in dark mode. Keep the
   * override local to this page so authenticated screens still use the
   * global theme selection.
   */
  const loginTheme = useMemo<AppTheme>(() => {
    return getThemepalette('light')
  }, [])

  return (
    <Layout>
      <ThemeProvider theme={loginTheme}>
        <LoginShell aria-label='Student login'>
          <LoginScrim />
          <LoginPageFrame>
            <Brand aria-label='Alpha Plus Educational Systems'>
              <img src={loginLogo} alt='Alpha Plus Educational Systems' />
            </Brand>

            <LoginMain>
              <LoginCard>
                <LoginCardBody>
                  <LoginTitle>Student Login</LoginTitle>
                  {errors.errorMessage.length > 0 && (
                    <LoginAlert
                      dismissible
                      onClose={() => dispatch(clearError())}
                      variant='danger'
                    >
                      {errors.errorMessage}
                    </LoginAlert>
                  )}

                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={onSubmit}
                  >
                    {({
                      values,
                      handleChange,
                      errors,
                      submitCount,
                      validateForm
                    }) => {
                      const shouldShowValidation =
                        hasLoginAttempted || submitCount > 0
                      const canSubmit =
                        Boolean(values?.last_name) &&
                        isValidStudentId(values.student_id)
                      const isSubmitDisabled = isLoading || !canSubmit

                      return (
                        <LoginForm>
                          <FieldGroup>
                            <FieldLabel htmlFor='last-name'>
                              Last Name
                            </FieldLabel>
                            <Field
                              as={TextInput}
                              id='last-name'
                              name='last_name'
                              type='text'
                              placeholder='Enter Last Name'
                              aria-describedby='last-name-error'
                              className={
                                shouldShowValidation && errors.last_name
                                  ? 'is-invalid'
                                  : ''
                              }
                            />
                            <StatusDot
                              $hasError={Boolean(errors.last_name)}
                              $visible={
                                shouldShowValidation &&
                                Boolean(errors.last_name)
                              }
                            />
                            {shouldShowValidation && (
                              <ErrorMessage
                                name='last_name'
                                render={(message) => (
                                  <FieldError id='last-name-error'>
                                    {message}
                                  </FieldError>
                                )}
                              />
                            )}
                          </FieldGroup>

                          <FieldGroup>
                            <FieldLabel htmlFor='student-id'>
                              Last 5 of Student ID
                            </FieldLabel>
                            <Field
                              as={TextInput}
                              id='student-id'
                              name='student_id'
                              type='text'
                              inputMode='numeric'
                              placeholder='Enter Last 5'
                              aria-describedby='student-id-error'
                              className={
                                shouldShowValidation && errors.student_id
                                  ? 'is-invalid'
                                  : ''
                              }
                              onChange={(e: any) => {
                                const val = e.target.value.trim().slice(0, 5)
                                e.target.value = val
                                handleChange(e)
                              }}
                            />
                            <StatusDot
                              $hasError={Boolean(errors.student_id)}
                              $visible={
                                shouldShowValidation &&
                                Boolean(errors.student_id)
                              }
                            />
                            {shouldShowValidation && (
                              <ErrorMessage
                                name='student_id'
                                render={(message) => (
                                  <FieldError id='student-id-error'>
                                    {message}
                                  </FieldError>
                                )}
                              />
                            )}
                          </FieldGroup>

                          <ButtonPanel>
                            <ButtonStack>
                              <ButtonRow>
                                <SsoButton
                                  href={`https://clever.com/oauth/authorize?response_type=code&redirect_uri=${config.defaults.redirect_url}&client_id=${config.defaults.clever_client_id}`}
                                  $provider='clever'
                                >
                                  <span>
                                    <svg
                                      xmlns='http://www.w3.org/2000/svg'
                                      width='15'
                                      height='17'
                                      viewBox='0 0 15 17'
                                      fill='none'
                                      aria-hidden='true'
                                      focusable='false'
                                    >
                                      <mask
                                        id='mask0_1115_8157'
                                        style={{ maskType: 'luminance' }}
                                        maskUnits='userSpaceOnUse'
                                        x='0'
                                        y='0'
                                        width='15'
                                        height='17'
                                      >
                                        <path
                                          d='M14.723 0.202148H0V16.7971H14.723V0.202148Z'
                                          fill='white'
                                        />
                                      </mask>
                                      <g mask='url(#mask0_1115_8157)'>
                                        <path
                                          fillRule='evenodd'
                                          clipRule='evenodd'
                                          d='M-0.00140192 8.54721C-0.0287173 7.44591 0.167661 6.35051 0.575853 5.32728C0.984045 4.30405 1.59557 3.37427 2.37342 2.59417C3.15128 1.81407 4.0793 1.19987 5.10135 0.788736C6.12339 0.377598 7.21822 0.178066 8.3196 0.202209C9.48358 0.145242 10.6464 0.336456 11.7308 0.763164C12.8152 1.18987 13.7965 1.84232 14.6096 2.67721L12.3776 5.26921C11.3092 4.15212 9.84374 3.50079 8.2986 3.45621C7.66316 3.47069 7.03711 3.61268 6.45761 3.87376C5.8781 4.13484 5.35696 4.50969 4.92511 4.97605C4.49326 5.44241 4.1595 5.99078 3.94366 6.58861C3.72781 7.18644 3.63428 7.82154 3.6686 8.45621C3.6686 11.2562 5.5586 13.5472 8.2986 13.5472C9.08918 13.5399 9.86972 13.3694 10.5913 13.0462C11.3128 12.723 11.9598 12.2542 12.4916 11.6692L14.7236 13.9372C13.9225 14.8812 12.9165 15.6299 11.7823 16.1264C10.6481 16.6228 9.41561 16.854 8.1786 16.8022C7.09398 16.8169 6.01752 16.6128 5.01352 16.2022C4.00952 15.7916 3.09857 15.1828 2.33507 14.4123C1.57156 13.6418 0.971161 12.7253 0.569724 11.7176C0.168287 10.7099 -0.0259552 9.63165 -0.00140192 8.54721Z'
                                          fill='currentColor'
                                        />
                                      </g>
                                    </svg>
                                  </span>
                                  <hr />
                                  <span>
                                    {isLoadingClever && cleverId
                                      ? 'Logging...'
                                      : 'Log in with Clever'}
                                  </span>
                                </SsoButton>
                                <SubmitButton
                                  type={canSubmit ? 'submit' : 'button'}
                                  aria-disabled={isSubmitDisabled}
                                  disabled={isLoading}
                                  onClick={() => {
                                    setHasLoginAttempted(true)
                                    if (!canSubmit) {
                                      validateForm()
                                    }
                                  }}
                                >
                                  {isLoading ? (
                                    <Spinner
                                      as='span'
                                      animation='border'
                                      size='sm'
                                      role='status'
                                      aria-hidden='true'
                                    />
                                  ) : (
                                    'Log In'
                                  )}
                                </SubmitButton>
                              </ButtonRow>

                              <ButtonRow $centered>
                                <SsoButton
                                  href={`https://launchpad.classlink.com/oauth2/v2/auth?redirect_uri=${config.defaults.classlink_redirect_url}&type=classlink&client_id=${config.defaults.classlink_client_id}&scope=full&response_type=code`}
                                  $provider='classlink'
                                >
                                  <span>
                                    <svg
                                      xmlns='http://www.w3.org/2000/svg'
                                      width='23'
                                      height='17'
                                      viewBox='0 0 23 17'
                                      fill='none'
                                      aria-hidden='true'
                                      focusable='false'
                                    >
                                      <path d='M6.77752 10.2148C5.87624 11.2588 6.77752 11.7559 6.77752 11.7559C6.80188 11.7808 6.8506 11.7808 6.87496 11.8057C6.72881 11.234 6.70445 10.712 6.77752 10.2148Z' />
                                      <path d='M12.5721 11.5574C11.8657 12.0297 11.0618 13.6454 11.0618 13.6454H10.6477C10.9157 13.024 11.0862 12.5268 11.208 12.1043C11.1105 12.154 11.0375 12.154 11.0375 12.154C10.2093 12.4523 9.35669 12.6014 8.60156 12.6014C9.38105 13.8194 10.8426 14.0431 10.8426 14.0431C12.8887 14.3165 14.4234 13.1979 15.2516 12.3528C13.5221 11.4083 12.5721 11.5574 12.5721 11.5574Z' />
                                      <path d='M19.1749 7.08346C19.1749 7.08346 20.5147 3.67812 17.4454 1.34161C17.4454 1.34161 12.5249 -1.96431 9.77234 3.55384C9.77234 3.55384 7.79926 1.54046 5.38772 3.10642C3.3172 4.42382 3.65823 6.23834 3.8531 7.13318C3.8531 7.13318 -0.0687026 8.30143 0.12617 12.5519C0.272324 15.7335 3.26848 16.6781 4.43772 16.9266C4.68131 16.9764 4.94926 17.0012 5.21721 17.0012H18.0788C18.3224 17.0012 18.5903 16.9764 18.8339 16.9266C20.0031 16.6781 23.048 15.7335 22.9993 12.3779C22.9262 8.202 19.1749 7.08346 19.1749 7.08346ZM10.7223 14.7144C8.11593 14.6896 7.19029 12.701 7.19029 12.701C7.14157 12.6016 7.09285 12.5022 7.04413 12.3779C6.33772 12.1045 5.89926 11.5825 5.99669 10.8368C6.09413 10.0662 6.9467 9.29569 6.9467 9.29569C7.43388 7.72973 8.55439 6.6609 8.55439 6.6609C8.55439 6.6609 9.48004 5.64178 10.5031 5.1198L12.2813 5.39322C8.26208 7.05861 8.06721 10.5137 8.06721 10.5137C8.04285 11.0356 8.11593 11.4831 8.21337 11.8559C8.50567 11.8311 8.72491 11.8062 8.72491 11.8062C9.72362 11.6571 10.5762 11.3836 11.307 11.0605C11.2826 10.3894 10.9903 10.2154 10.9903 10.2154C10.9903 10.2154 10.6249 9.79282 9.13901 9.54425L9.28516 9.3454C9.28516 9.3454 10.0159 9.5194 10.8685 9.3454C11.7941 9.17141 12.6223 8.52514 13.548 7.2326L13.9865 7.20775C13.0852 8.45057 13.0121 9.37026 13.1582 9.99167C14.0595 9.29569 14.498 8.69913 14.498 8.69913C16.13 6.36262 13.8647 6.71061 13.8647 6.71061L14.5467 5.89035C18.0057 6.6609 14.839 9.86739 14.839 9.86739C14.4736 10.2651 14.0595 10.5882 13.6454 10.8617C13.6941 10.9362 14.3031 11.5576 15.4724 12.1293C15.7647 11.8062 15.9352 11.5576 15.9352 11.5576L16.398 11.6322C13.6698 14.963 10.7223 14.7144 10.7223 14.7144ZM11.9647 8.07772C11.9403 8.47543 11.6236 8.7737 11.2582 8.79856C10.8441 8.79856 10.5031 8.45057 10.5031 8.02801C10.5275 7.63031 10.8441 7.33203 11.2095 7.30717C11.648 7.28231 11.989 7.63031 11.9647 8.07772Z' />
                                    </svg>
                                  </span>
                                  <hr />
                                  <span>
                                    {isLoadingClever && classlinkId
                                      ? 'Logging...'
                                      : 'Log in with Classlink'}
                                  </span>
                                </SsoButton>
                              </ButtonRow>
                            </ButtonStack>
                          </ButtonPanel>
                        </LoginForm>
                      )
                    }}
                  </Formik>
                </LoginCardBody>
              </LoginCard>
            </LoginMain>

            <LegalFooter>
              <Copyright>
                &copy; {new Date().getFullYear()} Alpha Plus Educational Systems
              </Copyright>
              <LegalText>
                NOTICE: School staff privacy, security of computer systems, and
                protection of student information is important to Alpha Plus. In
                order to maintain privacy and security at all times, Alpha Plus
                Educational Systems does not collect student identification
                information other than first and last names and identification
                code, which are provided by school district authorities. Alpha
                Plus does not share this information with any external entities.
                School officials must consider the age and functionality of
                devices students use in terms of privacy and confidentiality
                laws and regulations, including the Children’s Internet
                Protection Act at Children's Internet Protection Act at{' '}
                <a
                  href='https://www.fcc.gov/consumers/guides/childrens-internet-protection-act'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  FCC Children’s Internet Protection Act (CIPA)
                </a>
                . By using this website, any educator, parent, staff member or
                other authorized user downloading personally identifiable
                student data agrees to protect the information as required by
                applicable state and federal laws including the Family
                Educational Rights and Privacy Act (FERPA - 20 U.S.C. &#167;
                1232g; 34 CFR Part 99). ALL online and downloadable content
                protected by &copy; {new Date().getFullYear()} Alpha Plus
                Educational Systems.
              </LegalText>
            </LegalFooter>
          </LoginPageFrame>
        </LoginShell>
      </ThemeProvider>
    </Layout>
  )
}

export default LoginPage
